import { apiClient } from './client'
import { ApiError } from './errors'
import {
  filenameFromDisposition,
  readBlobText,
  saveBlob,
  type DownloadOutcome,
} from '../download'
import type { RecognitionArm, ResultField } from '@/types/domain'

const http = () => apiClient.axios

/** The two file formats `GET /v1/templates/:id/results` can produce besides
 *  its JSON. */
export type ExportFormat = 'csv' | 'xlsx'

/**
 * `method` as the gateway reads it, and the only three values it takes.
 *
 * Anything else — an empty `method=`, or the wrong case ("ALL") — is now a
 * 400 `unknown_method`. It used to fall through to "all", which is the one
 * wrong answer a narrowing switch must not give: "nur geprüfte" then
 * delivered every document, flagged ones included, and nothing in the file
 * said so.
 *
 * - `specific` narrows to `documentIds`.
 * - `validated` narrows to documents where **at least one** field carries a
 *   review. Not "every field reviewed" — and a reviewer who blanked a field
 *   counts, since that is a review. The filter runs on the delivered result,
 *   before an `arm` swaps the values in, so it always means "somebody has
 *   looked at this document".
 */
export type ExportMethod = 'all' | 'validated' | 'specific'

export interface ExportOptions {
  method?: ExportMethod
  /** Only with `method: 'specific'`; sent as one comma-separated parameter,
   *  which is how the gateway splits it. */
  documentIds?: string[]
  /** A second column per field with the recognizer's percentage. Off by
   *  default, matching the gateway: a delivery is a list of values. */
  includeConfidence?: boolean
  /**
   * Export what this recognition arm made of the same scans instead of the
   * delivered values, so the file matches the reading on screen.
   *
   * An arm with no stored reading at all on this template is a 400
   * `arm_not_run` — it used to export the delivered values under that arm's
   * name, which is a file that says one thing and contains another.
   * Documents the arm merely did not cover keep their own result, as before.
   */
  arm?: RecognitionArm | string
  /** Which model's reading of that arm. Only with `arm`; omitted, the newest
   *  reading per document wins across every model that ran it — the same rule
   *  the records table and a promotion use. */
  model?: string
}

/**
 * One document's row as `returnType=json` reports it: the template's fields
 * keyed by **label** (not by box id — the gateway applies the field map
 * server-side here, which is the reason this endpoint exists beside the
 * document's own `result`), plus a handful of fixed keys.
 *
 * `Prüfen` is "ja"/"nein" and `Prüfhinweis` the flagged fields in words —
 * German keys, written by the gateway for the spreadsheet's header row, not
 * translated here.
 */
export type ResultRow = Record<string, ResultField | string | number | undefined>

/** `returnType=json`, keyed by the id of the uploaded document. A multi-page
 *  upload contributes one row per analysed page, which is why the value is an
 *  array. */
export type ResultsJson = Record<string, ResultRow[]>

export interface ExportFile {
  blob: Blob
  /** The name the gateway gave the file (`export.xlsx`), or a name derived
   *  from the format when it sent none. */
  filename: string
}

function exportParams(options: ExportOptions): Record<string, string> {
  const params: Record<string, string> = { method: options.method ?? 'all' }
  if (options.documentIds?.length) {
    params.documentIds = options.documentIds.join(',')
  }
  // Sent only when true: the gateway compares against the literal "true",
  // and an absent parameter reads the same as "false" without saying
  // anything about a default the client does not own.
  if (options.includeConfidence) params.includeConfidence = 'true'
  if (options.arm) params.arm = String(options.arm)
  // Only alongside an arm: on its own it narrows nothing, and the gateway
  // ignores it, which would make a model picker look as though it worked.
  if (options.arm && options.model) params.model = options.model
  return params
}

export const exportsApi = {
  /**
   * The analysed values as JSON, with review precedence already applied into
   * each field's `value` by the gateway.
   *
   * `method: 'validated'` now really filters — a "nur geprüfte" switch maps
   * onto it directly, and means "at least one field reviewed" rather than
   * "fully checked". Worth saying so where a person picks it.
   */
  async json(templateId: string, options: ExportOptions = {}): Promise<ResultsJson> {
    const { data } = await http().get<ResultsJson>(`/v1/templates/${templateId}/results`, {
      params: { ...exportParams(options), returnType: 'json' },
    })
    return data
  },

  /**
   * Fetches an export as bytes. Separate from `download` so a caller can send
   * the file somewhere else — a preview, an upload — without a trip through
   * the filesystem.
   */
  async file(
    templateId: string,
    format: ExportFormat,
    options: ExportOptions = {},
  ): Promise<ExportFile> {
    const response = await http().get<Blob>(`/v1/templates/${templateId}/results`, {
      params: { ...exportParams(options), returnType: format },
      responseType: 'blob',
    })
    const disposition = response.headers?.['content-disposition'] as string | undefined
    return {
      blob: response.data,
      filename: filenameFromDisposition(disposition) ?? `export.${format}`,
    }
  },

  /**
   * Fetches an export and hands it to the browser.
   *
   * The outcome is returned rather than swallowed because on iOS the file
   * opens in a tab instead of being saved, and only the caller knows where to
   * say so.
   */
  async download(
    templateId: string,
    format: ExportFormat,
    options: ExportOptions = {},
  ): Promise<DownloadOutcome> {
    const { blob, filename } = await exportsApi.file(templateId, format, options)
    return saveBlob(blob, filename)
  },
}

/**
 * A user-facing German sentence for the refusals this endpoint has of its own.
 *
 * Only the ones that mean something specific are named; anything else falls
 * back to the message the gateway sent, which `ApiError` has already picked
 * out of the body.
 */
export function exportErrorMessage(error: unknown): string {
  const apiError = error instanceof ApiError ? error : null
  if (!apiError) return 'Der Export konnte nicht erstellt werden.'
  if (apiError.code === 'unknown_arm') {
    return 'Diese Lesung ist unbekannt und kann nicht exportiert werden.'
  }
  if (apiError.code === 'arm_not_run') {
    // Not "keine Ergebnisse": the values exist, they were simply never read
    // this way. Exporting the delivered values under the arm's name is what
    // the gateway stopped doing.
    return 'Diese Lesung wurde für diese Vorlage noch nicht ausgeführt.'
  }
  if (apiError.code === 'unknown_method') {
    return 'Diese Auswahl wird nicht unterstützt. Bitte „alle“, „nur geprüfte“ oder eine Auswahl von Dokumenten wählen.'
  }
  if (apiError.status === 404) return 'Die Vorlage wurde nicht gefunden.'
  if (apiError.status === 503) {
    return 'Der Export ist derzeit nicht verfügbar (Datenbank nicht verbunden).'
  }
  return apiError.message
}

/**
 * The error body of a binary request arrives as a Blob, not as JSON, because
 * `responseType: 'blob'` applies to the failure too — so `ApiError.message`
 * would be axios's own "Request failed with status code 400" and the
 * gateway's `{error, code}` would be lost inside the blob.
 *
 * Callers that show the reason of a failed file export read it with this,
 * which re-parses the body and returns a fresh error carrying the gateway's
 * code and message.
 */
export async function readBlobError(error: unknown): Promise<ApiError> {
  const apiError = ApiError.from(error)
  const body = (apiError.cause as { response?: { data?: unknown } } | undefined)?.response?.data
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return apiError
  try {
    const parsed = JSON.parse(await readBlobText(body)) as Record<string, unknown>
    return new ApiError({
      status: apiError.status,
      message: (parsed.error as string) ?? (parsed.message as string) ?? apiError.message,
      code: parsed.code as string | undefined,
      details: parsed,
      cause: apiError.cause,
    })
  } catch {
    // Not JSON after all — a proxy's HTML error page, say. The original
    // error is still the better answer than nothing.
    return apiError
  }
}
